import { Injectable } from '@angular/core';
import { Subscription, interval } from 'rxjs';
import { ExecutionHashService } from './execution-hash.service';
import { LoadingService } from './loading.service';

declare var totvstec: any; //-- Variável disponível apenas quando a aplicação é executada dentro do Protheus

/**
 * Payload devolvido pelo Protheus ao fechar a interface nativa.
 * O campo `executionHash` é sempre preenchido pela ponte para correlacionar
 * a resposta com a chamada que a originou.
 */
export interface ProtheusBridgePayload {
  executionHash?: string;
  [key: string]: any;
}

/**
 * Opções para controlar o ciclo de uma chamada `jsToAdvpl` e seu retorno.
 * Todos os campos são opcionais: o caso mais simples só precisa de `onClosed`.
 */
export interface ProtheusBridgeOptions {
  /**
   * Atalho para controlar uma variável de loading do componente.
   * Ex.: `setLoading: (v) => this.loading = v`.
   * A ponte chama com `true` ao disparar e `false` ao concluir/erro.
   */
  setLoading?: (value: boolean) => void;

  /**
   * Quando `true`, também aciona o LoadingService global (BehaviorSubject).
   * Útil quando o componente usa o overlay de loading compartilhado.
   */
  useGlobalLoading?: boolean;

  /**
   * Executado quando o Protheus devolve o payload de fechamento correlacionado
   * ao `executionHash` desta chamada. É aqui que mora a lógica específica de
   * cada tela (recarregar lista, reabrir modal, notificar, etc.).
   */
  onClosed?: (payload: ProtheusBridgePayload) => void;

  /**
   * Executado se `totvstec.jsToAdvpl` não estiver disponível ou lançar erro,
   * ou se o payload do sessionStorage não puder ser interpretado.
   */
  onError?: (error: unknown) => void;

  /**
   * Sempre executado ao encerrar o fluxo (após onClosed ou onError),
   * independente de sucesso. Bom lugar para desligar loadings adicionais.
   */
  onFinally?: () => void;

  /**
   * Intervalo (ms) de verificação do sessionStorage. Padrão: 1000ms,
   * mantendo o comportamento legado.
   */
  pollingInterval?: number;
}

/**
 * Centraliza a comunicação com o Protheus via `totvstec.jsToAdvpl` e a espera
 * pelo retorno gravado em sessionStorage.
 *
 * Responsabilidades:
 *  - Injetar automaticamente o `executionHash` no corpo enviado (obrigatório).
 *  - Disparar a ação `jsToAdvpl` com o payload serializado.
 *  - Escutar o sessionStorage até o retorno correlacionado chegar.
 *  - Delegar a lógica específica de fechamento via callbacks plugáveis.
 *  - Gerenciar loading de forma reutilizável (callback local e/ou global).
 */
@Injectable({
  providedIn: 'root',
})
export class ProtheusBridgeService {
  constructor(
    private executionHashService: ExecutionHashService,
    private loadingService: LoadingService
  ) {}

  /**
   * Dispara uma ação para o Protheus e aguarda o retorno correlacionado.
   *
   * @param action Nome da ação `jsToAdvpl` (ex.: 'editQuote', 'updateContact').
   *               A chave lida do sessionStorage no retorno é `${action}Closed`.
   * @param data   Corpo a ser enviado. O `executionHash` é injetado automaticamente.
   * @param options Callbacks e controle de loading. Opcional.
   * @returns A Subscription do polling, permitindo cancelamento manual. A ponte
   *          já faz unsubscribe automático ao concluir.
   */
  callAndWait(
    action: string,
    data: ProtheusBridgePayload = {},
    options: ProtheusBridgeOptions = {}
  ): Subscription | null {
    const executionHash = this.executionHashService.getExecutionHash();
    const body = { ...data, executionHash };

    this.setLoading(options, true);

    try {
      totvstec.jsToAdvpl(action, JSON.stringify(body));
    } catch (error) {
      this.setLoading(options, false);
      this.handleError(options, error);
      this.runFinally(options);
      return null;
    }

    return this.waitForClose(action, executionHash, options);
  }

  /**
   * Apenas dispara a ação, sem aguardar retorno. Útil para chamadas
   * "fire and forget" (ex.: download) que não dependem do fechamento.
   */
  send(action: string, data: ProtheusBridgePayload = {}): void {
    const body = { ...data, executionHash: this.executionHashService.getExecutionHash() };
    totvstec.jsToAdvpl(action, JSON.stringify(body));
  }

  //-- Escuta o sessionStorage até o payload de fechamento correlacionado chegar.
  private waitForClose(
    action: string,
    executionHash: string,
    options: ProtheusBridgeOptions
  ): Subscription {
    const storageKey = `${action}Closed`;
    const period = options.pollingInterval ?? 1000;

    //-- Reset da chave para não reaproveitar retorno de execuções anteriores.
    sessionStorage.setItem(storageKey, 'undefined');

    const subscription = interval(period).subscribe(() => {
      const raw = sessionStorage.getItem(storageKey);

      if (raw === null || raw === 'undefined') {
        return;
      }

      let payload: ProtheusBridgePayload;
      try {
        payload = JSON.parse(raw);
      } catch (error) {
        subscription.unsubscribe();
        this.setLoading(options, false);
        this.handleError(options, error);
        this.runFinally(options);
        return;
      }

      //-- Só reage ao retorno desta execução específica.
      if (payload?.['executionHash'] !== executionHash) {
        return;
      }

      subscription.unsubscribe();
      this.setLoading(options, false);

      try {
        options.onClosed?.(payload);
      } finally {
        this.runFinally(options);
      }
    });

    return subscription;
  }

  //-- Aplica loading via callback local e/ou LoadingService global.
  private setLoading(options: ProtheusBridgeOptions, value: boolean): void {
    options.setLoading?.(value);

    if (options.useGlobalLoading) {
      this.loadingService.loading(value);
    }
  }

  private handleError(options: ProtheusBridgeOptions, error: unknown): void {
    if (options.onError) {
      options.onError(error);
    } else {
      console.error('[ProtheusBridgeService] Falha na chamada jsToAdvpl:', error);
    }
  }

  private runFinally(options: ProtheusBridgeOptions): void {
    options.onFinally?.();
  }
}
